import {makeAutoObservable, reaction, runInAction} from 'mobx'
import config, {systemObjects} from 'src/config'
import {MainStore} from 'src/store/MainStore'
import CodeObject from '../entities/CodeObject'
import Payment, {PaymentTab} from 'src/entities/payment'
import {downloaFile} from 'src/utils/export'
import {MonthlyPaymentSummary} from 'src/entities/MonthlyPaymentSummary'
import {CalcPayment, ExternalPayments} from 'src/entities/CalcPayment'
import React from 'react'
import {defaultMonth} from 'src/utils/month'
import Month from 'src/entities/Month'

export class PaymentsStore {
  payments?: Payment[]

  calcPayments?: CalcPayment[]

  externalPayments?: ExternalPayments[]

  paymentsStatuses: CodeObject[] = []

  isLoadingExportReport?: boolean

  isLoadingSendPaymentEmail?: boolean

  isInvalidPaymentReportId?: boolean

  isDeletePayment?: boolean = false

  selectedTab: PaymentTab = PaymentTab.Payments

  monthlyPaymentsSummary: MonthlyPaymentSummary[] = []

  saveButtonRef: React.RefObject<HTMLButtonElement> = React.createRef()

  amount: number | undefined = undefined

  selectedMonth: Month | null = defaultMonth

  previewedPayment: Payment | null = null

  isPreviewLoading: boolean = false

  constructor(readonly owner: MainStore) {
    makeAutoObservable(this)

    reaction(
      () => this.owner.loginStore.isMsalAuth,
      isAuth => {
        if (isAuth) {
          this.getPaymentsStatuses()
          this.getPayments()
        }
      }
    )

    reaction(
      () => this.owner.institutionStore.currentInstitution,
      currentInstitution => {
        this.setAmount(undefined)
        this.setSelectedMonth(defaultMonth)
        if (currentInstitution) {
          this.getCalcPayments()
          this.getPayments()
          this.getPaymentsExternal()
        }
      }
    )
  }

  focusSaveButton = () => {
    this.saveButtonRef.current?.focus()
  }

  calculateInstitutionPayment = async (
    institutionId: number,
    month: number,
    year: number,
    save: boolean = false,
    overrideAmount?: number,
    overrideReason?: string
  ): Promise<Payment | undefined> => {
    try {
      const params = new URLSearchParams()
      params.set('save', String(save))
      if (overrideAmount !== undefined) {
        params.set('overrideAmount', String(overrideAmount))
        if (overrideReason) params.set('overrideReason', overrideReason)
      }
      const res = await this.owner.loginStore.fetchWithUser(
        `${
          config.apiUrl
        }/Payments/calculate/${institutionId}/${month}/${year}?${params.toString()}`,
        {method: 'POST'}
      )
      if (!res.ok) return undefined
      const data = await res.json()
      return new Payment(data, this.paymentsStatuses)
    } catch (e) {
      console.error('calculateInstitutionPayment failed:', e)
      this.owner.setMessage('חישוב התשלום נכשל')
      return undefined
    }
  }

  previewPayment = async (
    institutionId: number,
    month: number,
    year: number
  ) => {
    runInAction(() => {
      this.isPreviewLoading = true
    })
    const result = await this.calculateInstitutionPayment(
      institutionId,
      month,
      year,
      false
    )
    runInAction(() => {
      this.previewedPayment = result || null
      this.isPreviewLoading = false
      if (result) {
        this.amount = result.amount
      }
    })
    return result
  }

  clearPreview = () => {
    this.previewedPayment = null
  }

  setAmount = (value: number | undefined) => {
    this.amount = value
  }

  setSelectedMonth = (value: Month | null) => {
    this.selectedMonth = value
    this.amount = undefined
  }

  setIsLoadingSendPaymentEmail = (isLoading: boolean) => {
    this.isLoadingSendPaymentEmail = isLoading
  }

  setIsInvalidPaymentReportId = (isInvalid: boolean) => {
    this.isInvalidPaymentReportId = isInvalid
  }

  setIsDeletePayment = (isDeletePayment: boolean) => {
    this.isDeletePayment = isDeletePayment
  }

  setSelectedTab = (tab: PaymentTab) => {
    this.selectedTab = tab
  }

  setCalcPayments(payments: CalcPayment[]) {
    this.calcPayments = payments
    this.calcPayments?.forEach(calc => {
      calc.setAllpayments(this.calcPayments ?? [])
    })
  }

  private mapToServerModel(calc: any) {
    return {
      Id: calc.id,
      InstitutionId: calc.institutionId,
      Month: calc.month,
      MonthString: calc.monthString,
      NumericYear: calc.numericYear ?? calc.year,
      TextYear: calc.textYear ?? '',
      BaseOnMonths: calc.baseOnMonths.map((baseOn: any) => ({
        Month: baseOn?.month,
        MonthString: baseOn?.monthString,
        NumericYear: baseOn?.numericYear ?? baseOn?.year,
        TextYear: baseOn?.textYear ?? '',
        IsDefault: baseOn?.isDefault,
        CalcPayAmount: baseOn?.calcPayAmount
      })),
      PaymentAmount: calc.paymentAmount,
      AllPayments: calc.allPayments
    }
  }

  setDefaultMonth = (calcPaymentId: number, month: number) => {
    const updatedPayments =
      this.calcPayments?.map(calc =>
        calc.id === calcPaymentId
          ? new CalcPayment(
              this.mapToServerModel({
                ...calc,
                baseOnMonths: calc.baseOnMonths.map(baseOn => ({
                  ...baseOn,
                  isDefault: baseOn.month === month
                }))
              }),
              this.calcPayments ?? [],
              this.externalPayments || []
            )
          : calc
      ) || []

    this.setCalcPayments(updatedPayments)
  }

  getPayments = async () => {
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments`
      )
      const data = await res.json()
      if (data) {
        runInAction(() => {
          this.payments = data.map((d: any) => {
            return new Payment(d, this.paymentsStatuses)
          })
        })
      }
    } catch (e) {
      console.log('error:', e)
    }
  }

  private getIsDefault(
    existingPayment: CalcPayment | undefined,
    month: number,
    baseOnDefault: boolean
  ) {
    const prevIsDefault = existingPayment?.baseOnMonths.find(
      b => b.month === month
    )?.isDefault
    return prevIsDefault !== undefined ? prevIsDefault : baseOnDefault
  }

  getCalcPayments = async (usePrevious: boolean = false) => {
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/CalcPayments/${this.owner.institutionStore.currentInstitution?.id}`
      )
      const data = await res.json()
      if (data) {
        runInAction(() => {
          let allPayments: CalcPayment[] = []
          data.forEach((d: any, index: number) => {
            const existingPayment = this.calcPayments?.find(
              c => c.id === index + 1
            )

            allPayments.push(
              new CalcPayment(
                {
                  ...d,
                  Id: index + 1,
                  BaseOnMonths: !usePrevious
                    ? d.BaseOnMonths
                    : d.BaseOnMonths.map((baseOn: any) => ({
                        ...baseOn,
                        IsDefault: this.getIsDefault(
                          existingPayment,
                          baseOn.Month,
                          baseOn.IsDefault
                        )
                      }))
                },
                allPayments,
                this.externalPayments || []
              )
            )
          })
          this.calcPayments = allPayments
        })
      }
    } catch (e) {
      console.log('error:', e)
    }
  }

  getPaymentsExternal = async () => {
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/ExternalPayments/${this.owner.institutionStore.currentInstitution?.id}`
      )
      const data = await res.json()
      if (data) {
        runInAction(() => {
          this.externalPayments = data.map((i: any) => new ExternalPayments(i))
        })
      }
    } catch (e) {
      console.log('error:', e)
    }
  }

  createExternalPayment = async (externalPayment: any) => {
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/ExternalPayment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            InstitutionId: this.owner.institutionStore.currentInstitution?.id,
            NumericYear: externalPayment.numericYear,
            Month: externalPayment.month,
            Amount: externalPayment.amount,
            Comment: externalPayment.comment
          })
        }
      )
      const data = await response.json()
      if (data) {
        runInAction(() => {
          this.externalPayments?.push(new ExternalPayments(data))
        })
        this.owner.setMessage('התשלום נוסף בהצלחה.')
        return true
      }
    } catch (error) {
      console.log('error:', error)
      this.owner.setMessage('הוספת התשלום נכשלה, נסה שוב.')
      return false
    }
  }

  updateExternalPayment = async (externalPayment: any) => {
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/ExternalPayment`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            Id: externalPayment.id,
            InstitutionId: this.owner.institutionStore.currentInstitution?.id,
            NumericYear: externalPayment.numericYear,
            Month: externalPayment.month,
            Amount: externalPayment.amount,
            Comment: externalPayment.comment
          })
        }
      )
      const data = await response.json()
      if (data) {
        runInAction(() => {
          const index = this.externalPayments?.findIndex(
            p => p.id === externalPayment.id
          )
          if (index !== undefined && index !== -1 && this.externalPayments) {
            this.externalPayments[index] = new ExternalPayments(data)
          }
        })
        this.owner.setMessage('התשלום עודכן בהצלחה.')
        return true
      }
    } catch (error) {
      console.log('error:', error)
      this.owner.setMessage('עדכון התשלום נכשל, נסה שוב.')
      return false
    }
  }

  deleteExternalPayment = async (paymentId: number) => {
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/ExternalPayment/${paymentId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      if (!response.ok) {
        throw new Error()
      }
      runInAction(() => {
        this.externalPayments =
          this.externalPayments?.filter(p => p.id !== paymentId) || []
      })

      this.owner.setMessage('התשלום נמחק בהצלחה.')
      return true
    } catch (error) {
      console.log('error:', error)
      this.owner.setMessage('מחיקת התשלום נכשלה, נסה שוב.')
      return false
    }
  }

  get decidedPayments() {
    return this.payments?.filter(p => p.statusId === 1) || []
  }

  get decidedPaymentsCount() {
    return this.decidedPayments.length
  }

  get decidedPaymentsSum() {
    return this.decidedPayments.reduce(
      (sum, payment: any) => sum + (payment?.displayAmount ?? 0),
      0
    )
  }

  getPaymentsStatuses = () => {
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/CodeTables/${systemObjects.paymentStatuses}`
      )
      .then(res => {
        return res.json()
      })
      .then((data: any) => {
        if (data) {
          runInAction(() => {
            this.paymentsStatuses = data.map((i: any) => new CodeObject(i))
          })
        }
      })
      .catch(e => {
        console.log('error:', e)
      })
  }

  createPayment = (payment: any) => {
    this.owner.loginStore
      .fetchWithUser(`${config.apiUrl}/Payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payment)
      })
      .then(res => {
        return res.json()
      })
      .then((data: any) => {
        if (data) {
          runInAction(() => {
            this.payments?.push(
              new Payment(
                {
                  Id: data?.Id,
                  Amount: payment.amount,
                  StatusId: 1,
                  Month: payment.month,
                  NumericYear: payment.numericYear,
                  Comment: payment.comment,
                  InstitutionId: payment.institutionId,
                  CreatedAt: new Date(),
                  CreatedBy: payment.createdBy
                },
                this.paymentsStatuses
              )
            )
            this.getCalcPayments(true)
          })
        }
      })
      .catch((e: any) => {
        console.log('error: ', e)
        this.owner.setMessage('ההחלטה לא נשמרה, נסה שוב.')
      })
  }

  sendPaymentEmail = async (paymentReportId: number): Promise<boolean> => {
    return this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/Payments/SendPaymentEmails/${paymentReportId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      .then(res => {
        if (res.status === 200) {
          this.setIsInvalidPaymentReportId(false)
          this.getPayments()
          return true
        }
        this.owner.setMessage(
          `שליחת המיילים נכשלה (סטטוס ${res.status}), נסה שוב.`
        )
        return false
      })
      .catch((e: any) => {
        if (e.status === 404) {
          this.setIsInvalidPaymentReportId(true)
          return false
        }
        console.log('error: ', e)

        this.owner.setMessage('ארעה שגיאה בשליחת הדו"ח, נסה שוב.')
        return false
      })
      .finally(() => {
        this.setIsLoadingSendPaymentEmail(false)
      })
  }

  updatePayment = (payment: any) => {
    this.owner.loginStore
      .fetchWithUser(`${config.apiUrl}/Payments`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payment)
      })
      .then(res => {
        return res.json()
      })
      .then(() => {
        runInAction(() => {
          const paymentToUpdate = this.payments?.find(i => i.id === payment.id)
          if (paymentToUpdate) {
            Object.assign(
              paymentToUpdate,
              new Payment(
                {
                  Id: payment.id,
                  Amount: payment.amount,
                  Month: payment.month,
                  NumericYear: payment.numericYear,
                  Comment: payment.comment,
                  InstitutionId: payment.institutionId,
                  CreatedAt: paymentToUpdate.createdAt,
                  StatusId: paymentToUpdate.statusId
                },
                this.paymentsStatuses
              )
            )
          }
        })
      })
      .catch((e: any) => {
        console.log('error: ', e)
        this.owner.setMessage('ההחלטה לא נשמרה, נסה שוב.')
      })
  }

  getDashboardPaymentsByMonth = () => {
    this.owner.loginStore
      .fetchWithUser(`${config.apiUrl}/Payments/MonthlySummary`)
      .then((res: any) => {
        return res.json()
      })
      .then((data: MonthlyPaymentSummary[]) => {
        if (data) {
          runInAction(() => {
            this.monthlyPaymentsSummary = data.map(
              monthSummer => new MonthlyPaymentSummary(monthSummer)
            )
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  updatePaymentStatus = async (payment: Payment, statusId: number) => {
    const prevStatus = payment.statusId
    if (prevStatus === statusId) return

    payment.setStatus(statusId)

    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/${payment.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(statusId)
        }
      )
      const data = await response.json()
      if (data) {
      } else {
        payment.setStatus(prevStatus)
        throw new Error()
      }
    } catch (error) {
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  setIsLoadingExportReport = (isLoading: boolean) => {
    this.isLoadingExportReport = isLoading
  }

  exportReport = async () => {
    this.setIsLoadingExportReport(true)
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/createPaymentReport`,
        {
          method: 'POST'
        }
      )

      const blob = await response.blob()

      if (blob) {
        downloaFile(blob, 'PaymentsList')
        await this.getPayments()
        this.setIsLoadingExportReport(false)
      }
    } catch (error) {
      this.setIsLoadingExportReport(false)
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  deletePayment = async (paymentId: number) => {
    this.setIsDeletePayment(true)
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Payments/${paymentId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.ok) {
        throw new Error()
      }
      runInAction(() => {
        const newData =
          this.payments?.filter(payment => payment.id !== paymentId) || []
        this.payments = [...newData]
        return true
      })
      this.setIsDeletePayment(false)
    } catch (error) {
      this.setIsDeletePayment(false)
      this.owner.setMessage('מחיקת התשלום נכשלה, נסה שוב.')
    }
    return false
  }
}
